"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "./Button";
import { DashboardPreview } from "./DashboardPreview";

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

export function Hero() {
  const openCalendly = () => {
    const calendlyUrl = process.env.NEXT_PUBLIC_CALENDLY_URL || "https://calendly.com/sean89/30-minute-meeting";

    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: calendlyUrl });
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.onload = () => {
      window.Calendly?.initPopupWidget({ url: calendlyUrl });
    };
    document.head.appendChild(script);
  };

  return (
    <section className="pt-24 md:pt-32 lg:pt-40 pb-12 md:pb-16 lg:pb-24 bg-gradient-to-b from-bg to-bg-alt relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-[radial-gradient(circle,rgba(124,58,237,0.03)_0%,transparent_50%)]" />
        <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-[radial-gradient(circle,rgba(99,102,241,0.03)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 md:gap-12 lg:gap-16 xl:gap-20 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up text-center lg:text-left">
            <div className="inline-flex items-center gap-2 md:gap-2.5 bg-white border border-border px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-4 md:mb-6 text-xs md:text-[13px] font-medium text-text-secondary shadow-sm">
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-success rounded-full animate-pulse-dot" />
              <span>Introducing</span>
              <span className="text-primary font-semibold">Specledger 1.0</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] xl:text-[56px] font-extrabold leading-[1.1] mb-4 md:mb-6 tracking-tight">
              The Single Source of Truth for{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Spec-Driven Development
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-text-muted leading-relaxed mb-6 md:mb-8 max-w-[480px] mx-auto lg:mx-0">
              Surface critical human decision points early and clearly. Keep
              engineers, stakeholders, and AI agents aligned from requirements
              to implementation.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8 md:mb-12 justify-center lg:justify-start">
              <Button
                variant="primary"
                size="lg"
                href="https://app.specledger.io/login"
                className="w-full sm:w-auto"
              >
                Get Started Free <ArrowRight className="w-4 h-4 ml-1 inline" aria-hidden="true" />
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={openCalendly}
                className="w-full sm:w-auto"
              >
                Book a Demo
              </Button>
            </div>

            <div className="flex flex-row justify-center lg:justify-start gap-6 sm:gap-8 md:gap-12 pt-6 md:pt-8 border-t border-border">
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl md:text-[32px] font-extrabold text-primary tracking-tight tabular-nums">
                  10x
                </div>
                <div className="text-[11px] sm:text-xs md:text-[13px] text-text-muted mt-0.5 md:mt-1">
                  Faster spec-to-ship
                </div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl md:text-[32px] font-extrabold text-primary tracking-tight tabular-nums">
                  85%
                </div>
                <div className="text-[11px] sm:text-xs md:text-[13px] text-text-muted mt-0.5 md:mt-1">
                  Less rework
                </div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl md:text-[32px] font-extrabold text-primary tracking-tight tabular-nums">
                  3hrs
                </div>
                <div className="text-[11px] sm:text-xs md:text-[13px] text-text-muted mt-0.5 md:mt-1">
                  Saved per dev daily
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Dashboard Preview */}
          <div className="animate-fade-in-up delay-200 mt-4 lg:mt-0">
            <DashboardPreview />
          </div>
        </div>
      </div>
    </section>
  );
}
